import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { T, PageHeader, Alert } from '../components/UI';
import { confirmDelete } from '../utils/confirmDelete';

export const STATUS = {
  draft:     { label:'In progress',     color:'var(--muted)',  bg:'var(--bg)' },
  pending:   { label:'Awaiting review', color:'var(--amber)',  bg:'var(--amber-bg)' },
  approving: { label:'Approving…',      color:'var(--amber)',  bg:'var(--amber-bg)' },
  approved:  { label:'Approved',        color:'var(--green)',  bg:'var(--green-bg)' },
  rejected:  { label:'Sent back',       color:'var(--red)',    bg:'var(--red-bg)' },
};

export const StatusPill = ({ status }) => {
  const s = STATUS[status] || STATUS.draft;
  return <span style={{ display:'inline-block', padding:'3px 10px', borderRadius:'999px', fontSize:'11px', fontWeight:'600', color:s.color, background:s.bg, border:`1px solid ${s.color}33`, whiteSpace:'nowrap' }}>{s.label}</span>;
};

const FILTERS = [['all','All'], ['pending','Awaiting review'], ['approved','Approved'], ['rejected','Sent back'], ['draft','In progress']];

// Staff Registration → registrations submitted by staff themselves, plus the join code panel
export default function StaffRegistrationList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner  = user?.role === 'owner';

  const [regs,     setRegs]     = useState([]);
  const [loaded,   setLoaded]   = useState(false);
  const [notifyKiran, setNotifyKiran] = useState(false);
  const [shortLink,   setShortLink]   = useState('');
  const [kiranEmail,  setKiranEmail]  = useState('');
  const [filter,   setFilter]   = useState('all');
  const [loading,  setLoading]  = useState(true);
  const [notice,   setNotice]   = useState(null);
  const [copied,   setCopied]   = useState('');
  const [emailTo,  setEmailTo]  = useState('');
  const [sending,  setSending]  = useState(false);


  const load = useCallback(async () => {
    try {
      const [list, cfg] = await Promise.all([api.get('/staff-registrations'), api.get('/staff-registrations/config')]);
      setRegs(list.data.registrations || []);
      setLoaded(true);
      setNotifyKiran(!!cfg.data.notifyKiran);
      setShortLink(cfg.data.registrationLink || '');
      setKiranEmail(cfg.data.kiranEmail || '');
    } catch (err) {
      setNotice({ type:'error', text: err.response?.data?.message || 'Failed to load registrations' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Owner only (the server enforces it too). An approved registration's CS Staff record is kept.
  const handleDelete = async (r) => {
    const who = r.fullName || r.email;
    const safe = who.replace(/[&<>"']/g, ch => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[ch]));   // the dialog renders its message as HTML and the name was typed by the staff member
    if (!await confirmDelete(`The registration for "${safe}" and the uploaded licence files will be permanently removed.${r.status === 'approved' ? ' The CS Staff record is kept.' : ''}`)) return;
    try {
      await api.delete(`/staff-registrations/${r._id}`);
      setRegs(prev => prev.filter(x => x._id !== r._id));
      setNotice({ type:'success', text:`Registration for ${who} deleted.` });
    } catch (err) {
      setNotice({ type:'error', text: err.response?.data?.message || 'Failed to delete' });
    }
  };

  const copy = async (what, text) => {
    try { await navigator.clipboard.writeText(text); setCopied(what); setTimeout(()=>setCopied(''), 1800); }
    catch { setNotice({ type:'error', text:'Could not copy — please select and copy it manually' }); }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    setSending(true); setNotice(null);
    try {
      const { data } = await api.post('/staff-registrations/config/email', { emails: emailTo });
      setEmailTo('');
      setNotice({ type: data.failed?.length ? 'warning' : 'success',
        text: `Code sent to ${data.sent.join(', ')} — it works for ${data.hours} hours.`
          + (data.failed?.length ? ` Couldn’t send to ${data.failed.join(', ')}.` : '')
          + (data.skipped?.length ? ` Skipped ${data.skipped.join(', ')} (already a staff member).` : '') });
    } catch (err) {
      setNotice({ type:'error', text: err.response?.data?.message || 'Could not send the email' });
    } finally { setSending(false); }
  };

  const toggleKiran = async (on) => {
    setNotifyKiran(on);
    try { await api.put('/staff-registrations/config/notify', { kiran: on }); }
    catch (err) { setNotifyKiran(!on); setNotice({ type:'error', text: err.response?.data?.message || 'Could not change the email setting' }); }
  };

  const counts = regs.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {});
  const shown  = regs.filter(r => filter === 'all' || r.status === filter);

  const th = { padding:'10px 12px', textAlign:'left', fontSize:'11px', fontWeight:'500', textTransform:'uppercase', letterSpacing:'.5px', color:'var(--muted)', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' };
  const td = { padding:'11px 12px', fontSize:'13px', color:'var(--text)', verticalAlign:'middle' };
  const mono = { fontFamily:'JetBrains Mono,monospace' };
  const small = { ...T.btnSecondary, padding:'6px 10px', fontSize:'12px' };

  return (
    <div style={T.page}>
      <PageHeader title="Staff Registration"/>

      <div style={{ maxWidth:'1000px', margin:'0 auto', padding:'16px 16px 80px' }}>
        {notice && <Alert type={notice.type}>{notice.text}</Alert>}

        {/* Invite panel: every person gets their own code, emailed automatically */}
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'16px', marginBottom:'16px' }}>
          <div style={{ fontSize:'13px', fontWeight:'600', color:'var(--text)', marginBottom:'4px' }}>Invite staff to register</div>
          <div style={{ fontSize:'12px', color:'var(--muted)', lineHeight:1.5, marginBottom:'12px' }}>
            Type their email and press Send. Each person is emailed their own code (made automatically) and the link. The code is their login to the form — no password — and works for 72 hours or until they submit. To let someone change their details again later, send them a new code.
          </div>
          {loaded && (
            <>
              <div style={{ marginBottom:'14px' }}>
                <div style={{ fontSize:'11px', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:'4px' }}>Registration link (also in the email)</div>
                <div style={{ display:'flex', gap:'6px', alignItems:'center', maxWidth:'520px' }}>
                  <code style={{ ...mono, fontSize:'12px', flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'8px 10px' }}>{shortLink}</code>
                  <button onClick={()=>copy('link', shortLink)} style={small}>{copied==='link' ? 'Copied' : 'Copy'}</button>
                </div>
              </div>
              <form onSubmit={sendEmail}>
                <div style={{ fontSize:'11px', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:'4px' }}>Email a code</div>
                <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                  <input type="text" value={emailTo} onChange={e=>setEmailTo(e.target.value)} placeholder="name@example.com (separate several with commas)"
                    aria-label="Email addresses to send a registration code to" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                    style={{ ...T.input, flex:'1 1 240px', minWidth:0 }}/>
                  <button type="submit" disabled={sending || !emailTo.trim()} style={{ ...T.btnPrimary, opacity:sending||!emailTo.trim()?0.6:1 }}>
                    <i className="ti ti-send" aria-hidden="true"/> {sending ? 'Sending…' : 'Send code'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>

        {isOwner && (
          <label style={{ display:'flex', alignItems:'flex-start', gap:'10px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'12px 16px', marginBottom:'16px', cursor:'pointer' }}>
            <input type="checkbox" checked={notifyKiran} onChange={e=>toggleKiran(e.target.checked)} style={{ marginTop:'3px', width:'16px', height:'16px', accentColor:'var(--blue)' }}/>
            <span>
              <span style={{ display:'block', fontSize:'13px', fontWeight:'600', color:'var(--text)' }}>Also email Kiran about registrations</span>
              <span style={{ display:'block', fontSize:'12px', color:'var(--muted)', marginTop:'2px', lineHeight:1.5 }}>
                hr@carsaloon.com.au is always emailed when someone registers or updates their details. Turn this on to send the same email to {kiranEmail || 'Kiran'}.
              </span>
            </span>
          </label>
        )}

        {/* Filters */}
        <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'12px' }}>
          {FILTERS.map(([key, text]) => {
            const n = key === 'all' ? regs.length : (counts[key] || 0);
            const on = filter === key;
            return (
              <button key={key} onClick={()=>setFilter(key)}
                style={{ padding:'7px 12px', borderRadius:'999px', border:`1px solid ${on?'var(--blue)':'var(--border-md)'}`, background:on?'var(--blue-light)':'var(--surface)', color:on?'var(--blue)':'var(--muted)', fontSize:'12px', fontWeight:on?'600':'400', cursor:'pointer', fontFamily:'inherit' }}>
                {text} <span style={{ opacity:.7 }}>{n}</span>
              </button>
            );
          })}
        </div>

        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
          {loading ? (
            <div style={{ padding:'40px', textAlign:'center' }}><i className="ti ti-loader" style={{ fontSize:'24px', color:'var(--blue)' }} aria-hidden="true"/></div>
          ) : shown.length === 0 ? (
            <div style={{ padding:'48px', textAlign:'center' }}>
              <i className="ti ti-user-plus" style={{ fontSize:'32px', color:'var(--hint)', display:'block', marginBottom:'12px' }} aria-hidden="true"/>
              <div style={{ fontSize:'14px', fontWeight:'500', color:'var(--text)', marginBottom:'6px' }}>{regs.length ? 'Nothing in this view' : 'No registrations yet'}</div>
              <div style={{ fontSize:'12px', color:'var(--muted)' }}>{regs.length ? 'Try another filter.' : 'Share the link and code above with new staff.'}</div>
            </div>
          ) : (
            <div className="table-scroll">
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr>{[...['Name','Email','Mobile','Submitted','Status','Staff ID'], ...(isOwner ? [''] : [])].map(h => <th key={h || 'actions'} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {shown.map(r => (
                    <tr key={r._id} onClick={()=>navigate(`/staff-registrations/${r._id}`)} style={{ cursor:'pointer' }}
                      onMouseEnter={ev=>ev.currentTarget.style.background='var(--bg)'}
                      onMouseLeave={ev=>ev.currentTarget.style.background='transparent'}>
                      <td style={{ ...td, fontWeight:'500' }}>{r.fullName || <span style={{ color:'var(--hint)' }}>Not filled in yet</span>}</td>
                      <td style={td}>{r.email}</td>
                      <td style={td}><span style={{ ...mono, fontSize:'12px' }}>{r.mobilePhone || '—'}</span></td>
                      <td style={td}><span style={{ ...mono, fontSize:'12px' }}>{r.submittedAt ? new Date(r.submittedAt).toLocaleDateString('en-AU') : '—'}</span></td>
                      <td style={td}>
                        <StatusPill status={r.status}/>
                        {r.updatedAfterApproval && <span style={{ marginLeft:'6px', padding:'3px 8px', borderRadius:'999px', fontSize:'11px', fontWeight:'600', color:'var(--amber)', background:'var(--amber-bg)', border:'1px solid rgba(133,79,11,0.25)', whiteSpace:'nowrap' }}>Updated</span>}
                      </td>
                      <td style={td}><span style={{ ...mono, fontSize:'12px', fontWeight:'600', color:'var(--blue)' }}>{r.staffId || '—'}</span></td>
                      {isOwner && (
                        <td style={{ ...td, textAlign:'center' }} onClick={ev=>ev.stopPropagation()}>
                          <button onClick={()=>handleDelete(r)} disabled={r.status === 'approving'} title="Delete"
                            style={{ ...T.btnIcon, color:'var(--red)', borderColor:'rgba(163,45,45,0.25)', opacity:r.status === 'approving' ? 0.5 : 1 }}>
                            <i className="ti ti-trash" style={{ fontSize:'13px' }} aria-label="Delete"/>
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
